import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { ChevronLeft, ChevronRight, Trash2 } from "lucide-react";
import { API } from "../App";

const MONTHS = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];
const DAYS = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

function fmt(n) {
  return new Intl.NumberFormat("en-AU", {
    style: "currency", currency: "AUD", maximumFractionDigits: 0,
  }).format(Math.abs(n || 0));
}

export default function Calendar() {
  const now = new Date();
  // Default to previous month — most recent data is there
  const prevMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const [year, setYear]           = useState(prevMonthDate.getFullYear());
  const [month, setMonth]         = useState(prevMonthDate.getMonth() + 1);
  const [calData, setCalData]     = useState({});
  const [recurring, setRecurring] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [selectedDay, setSelectedDay] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const token = localStorage.getItem("taxbuddy_token");
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const [calRes, recRes] = await Promise.all([
        axios.get(`${API}/api/calendar?year=${year}&month=${month}`, { headers }),
        axios.get(`${API}/api/recurring`, { headers }),
      ]);
      setCalData(calRes.data || {});
      setRecurring(recRes.data?.recurring || []);
    } catch (err) {
      setCalData({});
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  const handleDeleteRecurring = async (id) => {
    const token = localStorage.getItem("taxbuddy_token");
    const headers = { Authorization: `Bearer ${token}` };
    try {
      await axios.delete(`${API}/api/recurring/${id}`, { headers });
      setRecurring(prev => prev.filter(r => (r.id || r._id) !== id));
    } catch {
      alert("Failed to remove recurring item");
    }
  };

  useEffect(() => { fetchData(); }, [fetchData]);

  const prevMonth = () => {
    if (month === 1) { setYear(y => y - 1); setMonth(12); }
    else setMonth(m => m - 1);
    setSelectedDay(null);
  };
  const nextMonth = () => {
    if (month === 12) { setYear(y => y + 1); setMonth(1); }
    else setMonth(m => m + 1);
    setSelectedDay(null);
  };

  const dateKey  = (d) => `${year}-${String(month).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
  const isToday  = (d) => now.getFullYear()===year && now.getMonth()+1===month && now.getDate()===d;

  const RECEIPT_RE = /^(visa\s+purchase|eftpos\s+purchase|direct\s+debit)\s*-\s*receipt\s+\d+/i;
  const stripBSB   = (s) => (s || "").replace(/^\d{6}\s+/, "");
  const isGarbled  = (s) => /e-?banking/i.test(s) || (s.length > 35 && !/\s/.test(s));
  const cleanRecurring = recurring.filter(r => !isGarbled(r.merchant || ""));

  const firstDow   = new Date(year, month - 1, 1).getDay();
  const daysInMon  = new Date(year, month, 0).getDate();
  const cells      = [
    ...Array(firstDow).fill(null),
    ...Array.from({ length: daysInMon }, (_, i) => i + 1),
  ];
  while (cells.length % 7 !== 0) cells.push(null);

  const selectedTxs = selectedDay ? (calData[dateKey(selectedDay)] || []) : [];

  const padMonth = String(month).padStart(2,"0");

  // Project recurring items into the viewed month (same logic as backend)
  // Cap sidebar at 10 most significant items by amount
  const FREQ_DAYS = { weekly: 7, fortnightly: 14, monthly: 30, quarterly: 91, annual: 365 };
  const predictedThisMonth = cleanRecurring.reduce((acc, r) => {
    if (!r.next_date || !r.frequency) return acc;
    const days = FREQ_DAYS[r.frequency];
    if (!days) return acc;
    const monthStart = new Date(year, month - 1, 1);
    const monthEnd = new Date(year, month, 0);
    let anchor = new Date(r.next_date + "T00:00:00");
    while (new Date(anchor.getTime() - days * 86400000) >= monthStart)
      anchor = new Date(anchor.getTime() - days * 86400000);
    let d = new Date(anchor);
    while (d <= monthEnd) {
      if (d >= monthStart)
        acc.push({ ...r, projected_date: d.toISOString().slice(0, 10) });
      d = new Date(d.getTime() + days * 86400000);
    }
    return acc;
  }, []);

  return (
    <div style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>
      {/* Header */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:20, flexWrap:"wrap", gap:12 }}>
        <div>
          <h1 style={{ fontSize:22, fontWeight:800, color:"#111827", margin:0 }}>Bills Calendar</h1>
          <p style={{ fontSize:13, color:"#6B7280", marginTop:4 }}>
            Actual transactions and recurring payment predictions
          </p>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          <button onClick={prevMonth} style={{ background:"#fff", border:"1px solid #E5E7EB", borderRadius:8, padding:"6px 10px", cursor:"pointer", display:"flex" }}>
            <ChevronLeft size={16} />
          </button>
          <span style={{ fontSize:15, fontWeight:700, color:"#111827", minWidth:160, textAlign:"center" }}>
            {MONTHS[month - 1]} {year}
          </span>
          <button onClick={nextMonth} style={{ background:"#fff", border:"1px solid #E5E7EB", borderRadius:8, padding:"6px 10px", cursor:"pointer", display:"flex" }}>
            <ChevronRight size={16} />
          </button>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign:"center", padding:60, color:"#9CA3AF", fontSize:14 }}>Loading…</div>
      ) : (
        <div style={{ display:"grid", gridTemplateColumns:"1fr 300px", gap:16, alignItems:"start" }}>
          {/* Calendar grid */}
          <div style={{ background:"#fff", border:"1px solid #E5E7EB", borderRadius:14, overflow:"hidden" }}>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", background:"#F9FAFB", borderBottom:"1px solid #E5E7EB" }}>
              {DAYS.map(d => (
                <div key={d} style={{ padding:"8px 0", textAlign:"center", fontSize:11, fontWeight:700, color:"#6B7280", textTransform:"uppercase" }}>{d}</div>
              ))}
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)" }}>
              {cells.map((d, i) => {
                const dk   = d ? dateKey(d) : null;
                const txs  = dk ? (calData[dk] || []) : [];
                const visibleTxs = txs.filter(t => t.merchant || !RECEIPT_RE.test(t.description || ""));
                const hasTx = visibleTxs.length > 0;
                const dayTotal = visibleTxs.reduce((s, t) => s + (t.type === "credit" ? t.amount : -t.amount), 0);
                const hasPredicted = dk ? (calData[dk] || []).some(t => t.isPrediction) : false;
                const isSel = selectedDay === d;
                const tod   = d && isToday(d);
                return (
                  <div
                    key={i}
                    onClick={() => d && setSelectedDay(isSel ? null : d)}
                    style={{
                      minHeight:68, padding:"6px 8px",
                      borderRight:(i+1)%7!==0 ? "1px solid #F3F4F6" : "none",
                      borderBottom:"1px solid #F3F4F6",
                      background: isSel ? "#EFF6FF" : tod ? "#FFFBEB" : d ? "#fff" : "#FAFAFA",
                      cursor: d ? "pointer" : "default",
                    }}
                  >
                    {d && (
                      <>
                        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                          <div style={{
                            fontSize:12, fontWeight: tod ? 800 : 500,
                            color: tod ? "#D97706" : "#374151",
                            width:22, height:22, display:"flex", alignItems:"center", justifyContent:"center",
                            borderRadius:"50%", background: tod ? "#FEF3C7" : "transparent",
                          }}>{d}</div>
                          {hasPredicted && !hasTx && (
                            <div title="Recurring payment predicted" style={{ width:6, height:6, borderRadius:"50%", background:"#8B5CF6", flexShrink:0 }} />
                          )}
                        </div>
                        {hasTx && (
                          <div style={{ marginTop:3 }}>
                            <div style={{ fontSize:10, fontWeight:700, color: dayTotal >= 0 ? "#059669" : "#374151" }}>
                              {dayTotal >= 0 ? "+" : ""}{fmt(dayTotal)}
                            </div>
                            {visibleTxs.slice(0, 2).map((t, j) => (
                              <div key={j} style={{ fontSize:9, color: t.type==="credit" ? "#059669" : "#374151", marginTop:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                                {(t.merchant || t.description || "").slice(0, 13)}
                              </div>
                            ))}
                            {visibleTxs.length > 2 && <span style={{ fontSize:9, color:"#9CA3AF" }}>+{visibleTxs.length-2}</span>}
                          </div>
                        )}
                        {hasPredicted && hasTx && (
                          <div title="Recurring payment predicted" style={{ width:6, height:6, borderRadius:"50%", background:"#8B5CF6", marginTop:2 }} />
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Side panel */}
          <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
            {/* Selected day */}
            {selectedDay ? (
              <div style={{ background:"#fff", border:"1px solid #E5E7EB", borderRadius:14, padding:16 }}>
                <h3 style={{ fontSize:13, fontWeight:700, color:"#111827", margin:"0 0 12px" }}>
                  {selectedDay} {MONTHS[month - 1]}
                </h3>
                {selectedTxs.length === 0 ? (
                  <p style={{ fontSize:12, color:"#9CA3AF", margin:0 }}>No transactions on this day.</p>
                ) : (
                  <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                    {selectedTxs.map((tx, i) => (
                      <div key={i} style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
                        <div style={{ flex:1, marginRight:8 }}>
                          <div style={{ fontSize:12, fontWeight:600, color:"#374151" }}>{tx.merchant || tx.description}</div>
                          <div style={{ fontSize:11, color:"#9CA3AF" }}>{(tx.category||"").replace(/_/g," ")}</div>
                        </div>
                        <div style={{ fontSize:13, fontWeight:700, color: tx.type==="credit" ? "#059669" : "#111827", whiteSpace:"nowrap" }}>
                          {tx.type==="credit" ? "+" : "-"}{fmt(tx.amount)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div style={{ background:"#F9FAFB", border:"1px dashed #E5E7EB", borderRadius:14, padding:16, textAlign:"center" }}>
                <p style={{ fontSize:12, color:"#9CA3AF", margin:0 }}>Click a day to see transactions</p>
              </div>
            )}

            {/* Recurring predicted this month */}
            <div style={{ background:"#fff", border:"1px solid #E5E7EB", borderRadius:14, padding:16 }}>
              <h3 style={{ fontSize:13, fontWeight:700, color:"#111827", margin:"0 0 12px" }}>
                Predicted this month
              </h3>
              {cleanRecurring.length === 0 ? (
                <p style={{ fontSize:12, color:"#9CA3AF", margin:0 }}>Upload more statements to detect recurring payments.</p>
              ) : predictedThisMonth.length === 0 ? (
                <p style={{ fontSize:12, color:"#9CA3AF", margin:0 }}>No recurring payments predicted this month.</p>
              ) : (
                <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                  {[...predictedThisMonth].sort((a, b) => b.average_amount - a.average_amount).slice(0, 10).map((r, i) => (
                    <div key={i} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"6px 0", borderBottom: i<predictedThisMonth.length-1 ? "1px solid #F3F4F6" : "none" }}>
                      <div>
                        <div style={{ fontSize:12, fontWeight:600, color:"#374151" }}>{stripBSB(r.merchant)}</div>
                        <div style={{ fontSize:11, color:"#9CA3AF" }}>{r.frequency} · {r.projected_date?.slice(8)} {MONTHS[month-1]}</div>
                      </div>
                      <div style={{ fontSize:13, fontWeight:700, color:"#111827" }}>-{fmt(r.average_amount)}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* All recurring summary */}
            {cleanRecurring.length > 0 && (
              <div style={{ background:"#fff", border:"1px solid #E5E7EB", borderRadius:14, padding:16 }}>
                <h3 style={{ fontSize:13, fontWeight:700, color:"#111827", margin:"0 0 12px" }}>
                  Recurring ({cleanRecurring.length})
                </h3>
                <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                  {cleanRecurring.map((r, i) => {
                    const rid = r.id || r._id;
                    return (
                      <div key={i} style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                        <div style={{ flex:1, minWidth:0 }}>
                          <span style={{ fontSize:12, color:"#374151", fontWeight:500 }}>{stripBSB(r.merchant)}</span>
                          <span style={{ fontSize:11, color:"#9CA3AF", marginLeft:6 }}>{r.frequency}</span>
                        </div>
                        <div style={{ display:"flex", alignItems:"center", gap:8, flexShrink:0 }}>
                          <span style={{ fontSize:12, color:"#6B7280", fontWeight:600 }}>-{fmt(r.average_amount)}</span>
                          <button
                            onClick={() => handleDeleteRecurring(rid)}
                            style={{ background:"none", border:"none", cursor:"pointer", color:"#9CA3AF", padding:2, display:"flex" }}
                            title="Remove"
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
